import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import { GiftCardStatus, type Customer, type GiftCard } from '@nucleus-commerce/api-client/admin';

import { customerListQueryOptions, customerName, customerQueryOptions } from '@/features/customers';

/** Fields GET /gift-cards sorts on; anything else is a 400. */
export const GIFT_CARD_SORTS = ['createdAt', 'balance', 'expiresAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key. Any `currency` marks `balance` as whole minor units; the page shows it
 * in the store's currency.
 */
export const GIFT_CARD_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(GiftCardStatus) },
  { key: 'customerId', type: 'async' },
  { key: 'balance', type: 'number-range', currency: 'USD' },
  { key: 'expiresAt', type: 'date-range' },
  { key: 'createdAt', type: 'date-range' },
] as const;

const customerOption = (customer: Customer): FilterOption => ({
  value: customer.id,
  label: customerName(customer) ?? customer.email,
  description: customer.email,
});

export interface GiftCardFiltersOptions {
  /** The `giftCards` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
  currency: string;
}

/** Filters of the gift cards table, in the order the filter menu lists them. */
export function giftCardFilters({
  t,
  queryClient,
  storeId,
  currency,
}: GiftCardFiltersOptions): Array<FilterDef<GiftCard>> {
  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: valuesOf(GiftCardStatus).map(({ value }) => ({
        value,
        label: t(`status.${value}`),
      })),
    },
    {
      key: 'customerId',
      label: t('filters.customer'),
      type: 'async',
      // The endpoint takes one customer.
      multiple: false,
      aliases: ['customer'],
      ...queryFilterOptions(queryClient, {
        search: (q) => customerListQueryOptions(storeId, { ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(customerOption),
        resolve: {
          query: ([id]) => customerQueryOptions(storeId, id ?? ''),
          toOptions: (customer) => [customerOption(customer)],
        },
      }),
    },
    { key: 'balance', label: t('filters.balance'), type: 'number-range', currency, min: 0 },
    { key: 'expiresAt', label: t('filters.expiresAt'), type: 'date-range', aliases: ['expires'] },
    { key: 'createdAt', label: t('filters.createdAt'), type: 'date-range', aliases: ['created'] },
  ];
}
